import React from 'react';
import { useNavigate } from 'react-router-dom';

const OPENING_VIDEO_URL = 'https://www.youtube.com/watch?v=e5ltUQbRDAo';
const OPENAI_PROMPT_GUIDANCE_URL = 'https://developers.openai.com/api/docs/guides/prompt-guidance';
const COPILOT_IMAGE_URL =
  'https://support.microsoft.com/ko-kr/microsoft-365-copilot/create-ai-generated-images-with-the-microsoft-365-copilot-app';
const COPILOT_VIDEO_URL =
  'https://support.microsoft.com/ko-kr/microsoft-365-copilot/create-a-video-with-the-microsoft-365-copilot-app';
const COPILOT_NOTEBOOK_URL =
  'https://support.microsoft.com/ko-kr/microsoft-365-copilot/get-started-with-microsoft-365-copilot-notebooks';
const COPILOT_TEACH_URL =
  'https://learn.microsoft.com/ko-kr/training/modules/create-instructional-materials-teach-microsoft-365-copilot/what-is-teach';

const M365_COPILOT_URL = 'https://m365.cloud.microsoft/';
const CHATGPT_URL = 'https://chatgpt.com/';
const CLAUDE_URL = 'https://claude.ai/';
const GEMINI_URL = 'https://gemini.google.com/';
const CANVA_URL = 'https://www.canva.com/';
const GETDESIGN_MD_URL = 'https://getdesign.md/';
const TRAINING_MATERIALS_DRIVE_URL =
  'https://drive.google.com/drive/folders/1VikZywia6L3ZLQy7jLSqL-Viozth3xcB?usp=sharing';
const SATISFACTION_SURVEY_URL = 'https://forms.gle/qUK1SDq56igoCuSM6';

/** 연수 발표 자료 PPT (OneDrive 공유 — 새 탭) */
const TRAINING_PPT_URL =
  'https://1drv.ms/p/c/f326a53b9af299ff/IQQq3tAw52v3T4XIGBP0ySr1AfU7Dq9N8SlSX67yXZ61Qeo';
const TRAINING_PPT_EMBED_SRC = `${TRAINING_PPT_URL}?wdAr=1.7777777777777777`;

const PERPLEXITY_URL = 'https://www.perplexity.ai/';
const COPILOT_URL = 'https://copilot.microsoft.com/';
const NOTEBOOKLM_URL = 'https://notebooklm.google.com/';

export const trainingMetadataJingwanAI2026 = {
  id: 'jingwan-middle-ai-2026',
  title: '진관중학교 AI 기반 수업 준비 및 자료 작성 연수',
  date: '2026-06-24',
  path: '/teacher-training/digital/JingwanMiddleSchoolAITraining2026',
};

const infoBoxStyle: React.CSSProperties = {
  backgroundColor: 'var(--Color-background-normal)',
  border: '1px solid var(--Color-line-normal)',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '14px',
  marginTop: '12px',
};

const thStyle: React.CSSProperties = {
  border: '1px solid var(--Color-line-normal)',
  padding: '10px 12px',
  backgroundColor: 'var(--Color-background-main)',
  textAlign: 'left',
  fontWeight: 600,
  color: 'var(--Color-text-strong)',
};

const tdStyle: React.CSSProperties = {
  border: '1px solid var(--Color-line-normal)',
  padding: '10px 12px',
  color: 'var(--Color-text-normal)',
  lineHeight: 1.6,
  verticalAlign: 'top',
};

const codeBlockStyle: React.CSSProperties = {
  margin: '12px 0 0 0',
  padding: '14px 16px',
  backgroundColor: 'var(--Color-background-main)',
  border: '1px solid var(--Color-line-normal)',
  borderRadius: '8px',
  fontSize: '13px',
  lineHeight: 1.7,
  color: 'var(--Color-text-normal)',
  whiteSpace: 'pre-wrap',
  fontFamily: 'Consolas, "Courier New", monospace',
  overflowX: 'auto',
};

const h2Style: React.CSSProperties = {
  fontWeight: 700,
  marginBottom: '12px',
  fontSize: '1.35rem',
  color: 'var(--Color-text-strong)',
};

const h3Style: React.CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: '17px',
  color: 'var(--Color-text-strong)',
};

const bodyTextStyle: React.CSSProperties = {
  color: 'var(--Color-text-normal)',
  marginBottom: '12px',
  lineHeight: 1.7,
  fontSize: '15px',
};

const quoteBoxStyle: React.CSSProperties = {
  ...infoBoxStyle,
  borderColor: 'var(--Color-fill-brand-color)',
  backgroundColor: '#fff8f0',
};

const JingwanMiddleSchoolAITraining2026: React.FC = () => {
  const navigate = useNavigate();

  const linkButton = (href: string, label: string) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-block',
        marginTop: '8px',
        marginRight: '8px',
        padding: '10px 18px',
        backgroundColor: 'var(--Color-fill-brand-color)',
        color: 'var(--Color-text-inverse)',
        textDecoration: 'none',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '14px',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      {label} →
    </a>
  );

  const quote = (text: string) => (
    <div style={quoteBoxStyle}>
      <p style={{ margin: 0, fontSize: '15px', color: 'var(--Color-text-strong)', lineHeight: 1.7, fontWeight: 600 }}>
        {text}
      </p>
    </div>
  );

  const codeBlock = (text: string) => <pre style={codeBlockStyle}>{text}</pre>;

  const tocItems: [string, string][] = [
    ['#연수-관련-링크', '연수 관련 링크'],
    ['#자료-다운-링크', '자료 다운 링크'],
    ['#연수-자료', '연수 발표 자료 (PPT)'],
    ['#연수-개요', '연수 개요'],
    ['#사용-도구', '사용 도구 바로가기'],
    ['#연수-순서', '100분 연수 순서'],
    ['#순서-1', '1. 오프닝 — 검색에서 제작으로'],
    ['#순서-2', '2. AI 5종 무료·유료 비교'],
    ['#순서-3', '3. Microsoft / Copilot 정리'],
    ['#순서-4', '4. 프롬프트 트렌드 — Context / Agent Design'],
    ['#순서-5', '5. 가정통신문 HWPX 실습'],
    ['#순서-6', '6. ChatGPT'],
    ['#순서-7', '7. Gemini — Gems · NotebookLM · Canvas'],
    ['#순서-8', '8. Claude — Artifacts · Skills · Cowork'],
    ['#순서-9', '9. Copilot — 이미지 · 영상 · Notebook · Teach'],
    ['#순서-10', '10. Perplexity 자료조사'],
    ['#순서-11', '11. PPT 제작 흐름 — 자료조사 → PPT 전문가'],
    ['#순서-12', '12. 바이브코딩과 업무 자동화'],
    ['#정리', '최종 메시지'],
    ['#만족도-조사', '연수 만족도 조사'],
  ];

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        padding: '24px',
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        fontFamily: 'var(--font-family-primary)',
        backgroundColor: 'var(--Color-background-main)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <button
          type="button"
          onClick={() => navigate('/teacher-training/training-pages')}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--Color-fill-normal)',
            color: 'var(--Color-text-inverse)',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          ← 돌아가기
        </button>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 700,
            margin: 0,
            color: 'var(--Color-text-strong)',
            lineHeight: 1.4,
          }}
        >
          {trainingMetadataJingwanAI2026.title}
        </h1>
      </div>

      <div
        style={{
          marginBottom: '24px',
          border: '1px solid var(--Color-line-normal)',
          padding: '16px',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-normal)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <h2 style={{ fontWeight: 700, marginBottom: '12px', fontSize: '1.125rem', color: 'var(--Color-text-strong)' }}>
          목차
        </h2>
        <ul style={{ listStyleType: 'none', padding: 0, margin: 0 }}>
          {tocItems.map(([hash, label]) => (
            <li key={hash} style={{ marginBottom: '8px' }}>
              <a
                href={hash}
                style={{
                  textDecoration: 'none',
                  color: 'var(--Color-fill-brand-color)',
                  fontWeight: 500,
                  fontSize: '15px',
                }}
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <h2 id="연수-관련-링크" style={h2Style}>
        연수 관련 링크
      </h2>
      <p style={bodyTextStyle}>오늘 연수에서 바로 사용하는 AI·디자인 도구입니다.</p>
      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {linkButton(M365_COPILOT_URL, 'Microsoft 365 Copilot')}
          {linkButton(CHATGPT_URL, 'ChatGPT')}
          {linkButton(CLAUDE_URL, 'Claude')}
          {linkButton(GEMINI_URL, 'Gemini')}
          {linkButton(CANVA_URL, 'Canva')}
          {linkButton(GETDESIGN_MD_URL, 'getdesign.md')}
        </div>
      </div>

      <h2 id="자료-다운-링크" style={h2Style}>
        자료 다운 링크
      </h2>
      <p style={bodyTextStyle}>연수 실습용 자료(HWPX 양식 등)는 Google Drive 폴더에서 내려받을 수 있습니다.</p>
      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        {linkButton(TRAINING_MATERIALS_DRIVE_URL, '연수 자료 Google Drive 폴더')}
      </div>

      <h2 id="연수-자료" style={h2Style}>
        연수 발표 자료 (PPT)
      </h2>
      <p style={{ color: 'var(--Color-text-normal)', marginBottom: '8px', lineHeight: 1.7, fontSize: '15px' }}>
        <a
          href={TRAINING_PPT_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--Color-fill-brand-color)', fontWeight: 600 }}
        >
          발표 자료 OneDrive에서 열기
        </a>
        <span style={{ color: 'var(--Color-text-normal)' }}> — 미리보기가 안 보이면 새 탭에서 여세요.</span>
      </p>
      <p style={{ fontSize: '13px', color: 'var(--Color-text-normal)', margin: '0 0 12px 0', lineHeight: 1.5 }}>
        페이지 안 미리보기(OneDrive 공식 임베드)
      </p>
      <div
        style={{
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid var(--Color-line-normal)',
          width: 'calc(100% + 48px)',
          marginLeft: '-24px',
          marginRight: '-24px',
          aspectRatio: '16 / 9',
          position: 'relative',
          backgroundColor: 'var(--Color-background-normal)',
          marginBottom: '32px',
        }}
      >
        <iframe
          title="PowerPoint Viewer"
          src={TRAINING_PPT_EMBED_SRC}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            display: 'block',
          }}
          loading="lazy"
          allowFullScreen
        />
      </div>

      <h2 id="연수-개요" style={h2Style}>
        연수 개요
      </h2>
      <div style={{ ...infoBoxStyle, marginBottom: '20px' }}>
        <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          주제: AI 기반 수업 준비 및 자료 작성
        </p>
        <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          대상: 진관중학교 교직원
        </p>
        <p style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          일시: 2026년 6월 24일
        </p>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          강사: 한지호
        </p>
      </div>

      <p style={bodyTextStyle}>
        이번 연수는 단순히 AI 서비스를 소개하는 자리가 아니라, 교사가 업무 목적에 따라 AI를 골라 쓰고, 자료와
        맥락을 제공하며, AI에게 역할을 나누어 맡겨 실제 산출물을 만드는 방법을 익히는 연수입니다.
      </p>
      {quote(
        'AI를 잘 쓰는 교사는 프롬프트를 외우는 교사가 아니라, 업무 문제를 잘 정의하고, 자료와 맥락을 잘 제공하며, AI에게 역할을 나누어 맡기는 교사다.',
      )}

      <h2 id="사용-도구" style={{ ...h2Style, marginTop: '32px' }}>
        사용 도구 바로가기
      </h2>
      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {linkButton(CHATGPT_URL, 'ChatGPT')}
          {linkButton(GEMINI_URL, 'Gemini')}
          {linkButton(CLAUDE_URL, 'Claude')}
          {linkButton(PERPLEXITY_URL, 'Perplexity')}
          {linkButton(COPILOT_URL, 'Copilot')}
          {linkButton(NOTEBOOKLM_URL, 'NotebookLM')}
        </div>
      </div>

      <h2 id="연수-순서" style={h2Style}>
        100분 연수 순서
      </h2>
      <table style={{ ...tableStyle, marginBottom: '32px' }}>
        <thead>
          <tr>
            <th style={thStyle}>순서</th>
            <th style={thStyle}>내용</th>
            <th style={thStyle}>시간</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>1</td>
            <td style={tdStyle}>오프닝: 바이브코딩과 AI 변화</td>
            <td style={tdStyle}>10분</td>
          </tr>
          <tr>
            <td style={tdStyle}>2</td>
            <td style={tdStyle}>AI 5종 무료/유료 비교 + Copilot 정리</td>
            <td style={tdStyle}>15분</td>
          </tr>
          <tr>
            <td style={tdStyle}>3</td>
            <td style={tdStyle}>프롬프트 최신 트렌드: Context / Agent Design</td>
            <td style={tdStyle}>10분</td>
          </tr>
          <tr>
            <td style={tdStyle}>4</td>
            <td style={tdStyle}>가정통신문 HWPX 실습</td>
            <td style={tdStyle}>15분</td>
          </tr>
          <tr>
            <td style={tdStyle}>5</td>
            <td style={tdStyle}>ChatGPT + GPTs</td>
            <td style={tdStyle}>10분</td>
          </tr>
          <tr>
            <td style={tdStyle}>6</td>
            <td style={tdStyle}>Gemini: Gems, NotebookLM, Canvas</td>
            <td style={tdStyle}>10분</td>
          </tr>
          <tr>
            <td style={tdStyle}>7</td>
            <td style={tdStyle}>Claude: Artifacts, Skills, Cowork</td>
            <td style={tdStyle}>10분</td>
          </tr>
          <tr>
            <td style={tdStyle}>8</td>
            <td style={tdStyle}>Copilot: 이미지, 영상, Notebook, Teach, Quiz</td>
            <td style={tdStyle}>15분</td>
          </tr>
          <tr>
            <td style={tdStyle}>9</td>
            <td style={tdStyle}>Perplexity 자료조사</td>
            <td style={tdStyle}>5분</td>
          </tr>
          <tr>
            <td style={tdStyle}>10</td>
            <td style={tdStyle}>PPT 제작 흐름 + 바이브코딩 사례</td>
            <td style={tdStyle}>10분</td>
          </tr>
        </tbody>
      </table>

      <h2 id="순서-1" style={h2Style}>
        1. 오프닝 — AI는 검색 도구에서 제작 도구로
      </h2>
      <p style={bodyTextStyle}>
        공무원이 바이브코딩으로 문제를 해결한 사례 영상으로 연수를 시작합니다.
      </p>
      <div style={{ marginBottom: '16px' }}>{linkButton(OPENING_VIDEO_URL, '오프닝 영상 보기')}</div>
      {quote('선생님들이 AI로 가장 줄이고 싶은 업무는 무엇인가요?')}
      <p style={bodyTextStyle}>예상 답변: 가정통신문, 공문, 보고서, PPT, 학습지, 평가문항, 세특, 엑셀 정리, 수업자료 제작</p>
      <p style={bodyTextStyle}>
        예전 AI는 주로 검색·요약 도구였지만, 지금은 문서·이미지·영상·PPT·수업안·퀴즈·웹앱·업무 자동화 도구를 만드는{' '}
        <strong>제작 도구</strong>로 바뀌고 있습니다.
      </p>
      {quote('이제 AI는 "물어보는 도구"가 아니라 문서·이미지·영상·앱을 함께 만드는 업무 파트너입니다.')}

      <h2 id="순서-2" style={h2Style}>
        2. AI 서비스 비교 — 무료와 유료
      </h2>
      <p style={bodyTextStyle}>비교 대상: ChatGPT, Gemini, Claude, Perplexity, Copilot</p>

      <h3 style={{ ...h3Style, marginTop: '20px' }}>무료 버전 비교</h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>서비스</th>
            <th style={thStyle}>무료 기준 강점</th>
            <th style={thStyle}>교사 활용</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>ChatGPT</td>
            <td style={tdStyle}>가장 범용적</td>
            <td style={tdStyle}>문서, 수업자료, 이미지, 코딩, 아이디어</td>
          </tr>
          <tr>
            <td style={tdStyle}>Gemini</td>
            <td style={tdStyle}>Google 생태계</td>
            <td style={tdStyle}>Drive, Docs, Gmail, NotebookLM</td>
          </tr>
          <tr>
            <td style={tdStyle}>Claude</td>
            <td style={tdStyle}>글쓰기와 긴 문서</td>
            <td style={tdStyle}>공문, 보고서, 문체 다듬기</td>
          </tr>
          <tr>
            <td style={tdStyle}>Perplexity</td>
            <td style={tdStyle}>출처 기반 검색</td>
            <td style={tdStyle}>최신 자료조사, 논문, 정책자료</td>
          </tr>
          <tr>
            <td style={tdStyle}>Copilot Chat</td>
            <td style={tdStyle}>Microsoft 기반 AI 채팅</td>
            <td style={tdStyle}>문서 초안, 요약, 이미지·영상 생성, Office 연계 가능성</td>
          </tr>
        </tbody>
      </table>

      <h3 style={{ ...h3Style, marginTop: '20px' }}>유료 버전 비교</h3>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>서비스</th>
            <th style={thStyle}>대표 유료 요금제</th>
            <th style={thStyle}>포지션</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>ChatGPT</td>
            <td style={tdStyle}>ChatGPT Plus</td>
            <td style={tdStyle}>가장 강력한 범용 AI</td>
          </tr>
          <tr>
            <td style={tdStyle}>Gemini</td>
            <td style={tdStyle}>Gemini AI Pro</td>
            <td style={tdStyle}>AI + Google 생태계 + NotebookLM</td>
          </tr>
          <tr>
            <td style={tdStyle}>Claude</td>
            <td style={tdStyle}>Claude Pro</td>
            <td style={tdStyle}>글쓰기, 긴 문서, 협업형 AI</td>
          </tr>
          <tr>
            <td style={tdStyle}>Perplexity</td>
            <td style={tdStyle}>Perplexity Pro</td>
            <td style={tdStyle}>검색·자료조사 특화</td>
          </tr>
          <tr>
            <td style={tdStyle}>Microsoft</td>
            <td style={tdStyle}>Microsoft 365 Personal / Family</td>
            <td style={tdStyle}>AI + Office + OneDrive</td>
          </tr>
        </tbody>
      </table>

      <div style={{ ...infoBoxStyle, marginTop: '16px', marginBottom: '32px' }}>
        <p style={{ margin: '0 0 8px 0', fontSize: '14px', lineHeight: 1.8, color: 'var(--Color-text-normal)' }}>
          <strong>ChatGPT</strong>는 AI를 사는 것 · <strong>Gemini</strong>는 Google 생태계를 사는 것
        </p>
        <p style={{ margin: '0 0 8px 0', fontSize: '14px', lineHeight: 1.8, color: 'var(--Color-text-normal)' }}>
          <strong>Microsoft 365</strong>는 Office와 AI를 같이 사는 것 · <strong>Claude</strong>는 글쓰기와 협업형 AI를
          사는 것
        </p>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: 1.8, color: 'var(--Color-text-normal)' }}>
          <strong>Perplexity</strong>는 검색과 출처 기반 조사를 사는 것
        </p>
      </div>

      <h2 id="순서-3" style={h2Style}>
        3. Microsoft / Copilot 정리
      </h2>
      <p style={bodyTextStyle}>Copilot은 이름이 헷갈리므로 연수 초반에 꼭 정리합니다.</p>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>구분</th>
            <th style={thStyle}>설명</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>Copilot 무료</td>
            <td style={tdStyle}>개인 Microsoft 계정으로 쓰는 일반 AI 챗</td>
          </tr>
          <tr>
            <td style={tdStyle}>학교 A3 Copilot Chat</td>
            <td style={tdStyle}>학교 계정으로 쓰는 Copilot Chat</td>
          </tr>
          <tr>
            <td style={tdStyle}>Microsoft 365 Personal / Family</td>
            <td style={tdStyle}>개인용 Office + Copilot 기능</td>
          </tr>
          <tr>
            <td style={tdStyle}>Microsoft 365 Copilot</td>
            <td style={tdStyle}>조직용. Word, Excel, PPT, Outlook, Teams, SharePoint 통합</td>
          </tr>
        </tbody>
      </table>
      <div style={{ ...quoteBoxStyle, marginTop: '16px' }}>
        <p style={{ margin: 0, fontWeight: 700, color: 'var(--Color-text-strong)' }}>
          학교 A3의 Copilot Chat ≠ Microsoft 365 Copilot
        </p>
        <p style={{ margin: '8px 0 0 0', fontSize: '14px', lineHeight: 1.7, color: 'var(--Color-text-normal)' }}>
          A3를 쓴다고 해서 자동으로 PowerPoint 안에서 PPT를 생성하거나, Outlook 메일함 전체를 요약하거나, Teams
          회의를 자동 정리하는 것은 아닙니다.
        </p>
      </div>
      <p style={{ ...bodyTextStyle, marginTop: '16px' }}>
        한국 학교는 주로 나이스·K-에듀파인·업무포털·공문시스템을 사용하므로, 조직용 Copilot의 가치가 상대적으로 줄어들
        수 있습니다.
      </p>
      {quote(
        'Teams와 Outlook을 적극적으로 쓰는 학교라면 Microsoft 365 Copilot이 강력하지만, 그렇지 않은 학교에서는 Microsoft 365 Personal/Family처럼 개인용 Office + AI 조합이 더 현실적일 수 있습니다.',
      )}

      <h2 id="순서-4" style={h2Style}>
        4. 프롬프트 최신 트렌드 — Context / Agent Design
      </h2>
      {quote(
        '프롬프트는 이제 "질문을 잘 쓰는 기술"이 아니라, AI가 참고할 자료, 사용할 도구, 출력 형식, 검증 기준을 함께 설계하는 업무 설계 능력으로 바뀌고 있다.',
      )}
      <p style={bodyTextStyle}>
        참고:{' '}
        <a href={OPENAI_PROMPT_GUIDANCE_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--Color-fill-brand-color)' }}>
          OpenAI Prompt Guidance
        </a>
      </p>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>4-1. Prompt Engineering → Context Engineering</h3>
        <p style={bodyTextStyle}>과거: &ldquo;가정통신문 작성해줘.&rdquo;</p>
        {codeBlock(`첨부한 HWPX 양식을 참고하여
1학년 공개수업 안내문을 작성해줘.

대상: 전 학부모
일시: 2026. 6. 15. 14:00~15:00
장소: 각 교실
분량: A4 1장 이내
문체: 학부모 안내문 형식
학교 양식 유지`)}
        <p style={{ ...bodyTextStyle, marginTop: '12px', marginBottom: 0 }}>
          이제 프롬프트는 &ldquo;AI에게 말 거는 문장&rdquo;이 아니라, <strong>AI에게 업무를 맡기기 위한 작업 지시서</strong>에
          가깝습니다.
        </p>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>4-2. 좋은 프롬프트의 6가지 조건</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>조건</th>
              <th style={thStyle}>설명</th>
              <th style={thStyle}>교사 업무 예시</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>목표</td>
              <td style={tdStyle}>무엇을 만들지 명확히</td>
              <td style={tdStyle}>공개수업 안내문</td>
            </tr>
            <tr>
              <td style={tdStyle}>맥락</td>
              <td style={tdStyle}>대상, 상황, 배경 제공</td>
              <td style={tdStyle}>학부모 대상, 학교 행사</td>
            </tr>
            <tr>
              <td style={tdStyle}>자료</td>
              <td style={tdStyle}>PDF, HWPX, 예시문 제공</td>
              <td style={tdStyle}>학교 가정통신문 양식</td>
            </tr>
            <tr>
              <td style={tdStyle}>형식</td>
              <td style={tdStyle}>표, 문서, 문항, PPT 구조 지정</td>
              <td style={tdStyle}>A4 1장, 표 포함</td>
            </tr>
            <tr>
              <td style={tdStyle}>검증</td>
              <td style={tdStyle}>오류·누락 확인 요청</td>
              <td style={tdStyle}>날짜, 장소, 대상 확인</td>
            </tr>
            <tr>
              <td style={tdStyle}>보안</td>
              <td style={tdStyle}>개인정보·민감정보 주의</td>
              <td style={tdStyle}>학생 실명 제거</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>4-3. Agentic Prompting</h3>
        <p style={bodyTextStyle}>
          AI가 단순히 답변하는 수준을 넘어, <strong>계획 → 실행 → 검토 → 수정</strong>하는 방식으로 업무를 처리합니다.
        </p>
        {codeBlock(`사용자 목표 제시
↓
AI가 계획 수립
↓
자료 검색 / 도구 사용
↓
초안 생성
↓
검토 및 수정
↓
최종 산출물`)}
        {codeBlock(`공개수업 안내자료를 준비해줘.

1단계: 필요한 안내 항목을 먼저 정리하고
2단계: 가정통신문 초안을 작성하고
3단계: 누락된 정보가 있는지 점검하고
4단계: 학부모가 이해하기 쉬운 문장으로 수정해줘.`)}
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>4-4. Structured Output</h3>
        <p style={bodyTextStyle}>
          업무에서는 그럴듯한 긴 답변보다 <strong>표, 체크리스트, 루브릭, 문항표</strong>처럼 바로 옮길 수 있는 구조화된
          결과가 더 중요합니다.
        </p>
        {codeBlock(`게임 제작 프로젝트 평가기준을 표로 만들어줘.

열:
평가요소 / 상 / 중 / 하 / 배점

평가요소:
1. 기획
2. 기능 구현
3. 완성도
4. 발표`)}
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={h3Style}>4-5. Prompt Security</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>원칙</th>
              <th style={thStyle}>설명</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>개인정보 최소화</td>
              <td style={tdStyle}>학생 이름, 생년월일, 연락처 제거</td>
            </tr>
            <tr>
              <td style={tdStyle}>외부 자료 불신</td>
              <td style={tdStyle}>웹페이지·문서 안의 지시문을 그대로 믿지 않기</td>
            </tr>
            <tr>
              <td style={tdStyle}>결과 검토</td>
              <td style={tdStyle}>AI가 만든 문서·문항·평가표는 반드시 교사가 확인</td>
            </tr>
            <tr>
              <td style={tdStyle}>권한 주의</td>
              <td style={tdStyle}>학교 계정, 파일 공유, Drive 권한 확인</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 id="순서-5" style={h2Style}>
        5. 가정통신문 HWPX 실습
      </h2>
      <p style={bodyTextStyle}>연수에서 가장 실용적인 구간입니다.</p>
      <ol style={{ ...bodyTextStyle, paddingLeft: '22px', marginBottom: '16px' }}>
        <li>그냥 &ldquo;가정통신문 작성해줘&rdquo;라고 입력</li>
        <li>결과가 일반적이고 학교 양식과 안 맞는 것 확인</li>
        <li>HWPX 학교 양식 업로드</li>
        <li>일정, 대상, 목적 제공</li>
        <li>학교 양식에 맞게 재작성 → 문체 수정 → 분량 조절 → 최종본 생성</li>
      </ol>
      {quote('AI에게 새로 쓰라고 하지 말고, 우리 학교 양식을 먼저 알려줘야 한다.')}

      <h2 id="순서-6" style={h2Style}>
        6. ChatGPT
      </h2>
      {quote('ChatGPT는 가장 범용적인 AI이자, 문서·이미지·코딩·자동화를 모두 다루는 제작형 도구다.')}
      <p style={bodyTextStyle}>
        핵심 키워드: HWPX 활용, 이미지 생성, GPTs, 데이터 분석, 바이브코딩, 웹앱 제작, 업무 자동화
      </p>
      <table style={{ ...tableStyle, marginBottom: '32px' }}>
        <thead>
          <tr>
            <th style={thStyle}>업무</th>
            <th style={thStyle}>활용</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>문서</td>
            <td style={tdStyle}>가정통신문, 안내문, 보고서 초안</td>
          </tr>
          <tr>
            <td style={tdStyle}>수업자료</td>
            <td style={tdStyle}>학습지, 퀴즈, 루브릭</td>
          </tr>
          <tr>
            <td style={tdStyle}>이미지</td>
            <td style={tdStyle}>수업 삽화, 포스터, 캐릭터</td>
          </tr>
          <tr>
            <td style={tdStyle}>데이터</td>
            <td style={tdStyle}>설문 분석, 엑셀 정리</td>
          </tr>
          <tr>
            <td style={tdStyle}>코딩</td>
            <td style={tdStyle}>웹앱, 간단한 자동화 도구</td>
          </tr>
          <tr>
            <td style={tdStyle}>GPTs</td>
            <td style={tdStyle}>반복 업무용 맞춤 GPT 제작</td>
          </tr>
        </tbody>
      </table>

      <h2 id="순서-7" style={h2Style}>
        7. Gemini — Gems · NotebookLM · Canvas
      </h2>
      {quote('Gemini는 단순 챗봇이 아니라 Google 생태계와 연결되는 업무·자료 기반 AI다.')}

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>7-1. Gemini 기본 강점</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>영역</th>
              <th style={thStyle}>활용</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Google Drive</td>
              <td style={tdStyle}>문서 기반 질의응답, 자료 정리</td>
            </tr>
            <tr>
              <td style={tdStyle}>Docs / Slides / Gmail / Sheets</td>
              <td style={tdStyle}>글쓰기, 발표자료, 메일, 표 정리</td>
            </tr>
            <tr>
              <td style={tdStyle}>NotebookLM</td>
              <td style={tdStyle}>자료 기반 연구·학습</td>
            </tr>
            <tr>
              <td style={tdStyle}>Canvas</td>
              <td style={tdStyle}>문서, 앱, 코드, 시각자료 제작</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>7-2. Gems — 내가 원하는 역할을 미리 설정해두는 Gemini용 맞춤 AI</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Gem 예시</th>
              <th style={thStyle}>활용</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>가정통신문 도우미</td>
              <td style={tdStyle}>안내문 초안, 문체 정리</td>
            </tr>
            <tr>
              <td style={tdStyle}>평가문항 도우미</td>
              <td style={tdStyle}>객관식·서술형 문항 생성</td>
            </tr>
            <tr>
              <td style={tdStyle}>학습지 도우미</td>
              <td style={tdStyle}>활동지 문항, 탐구 질문 생성</td>
            </tr>
            <tr>
              <td style={tdStyle}>수업 아이디어 도우미</td>
              <td style={tdStyle}>도입 활동, 모둠 활동 구상</td>
            </tr>
          </tbody>
        </table>
        {codeBlock(`너는 중학교 교사를 위한 평가문항 제작 도우미야.

앞으로 내가 단원과 학습목표를 입력하면
다음 형식으로 평가문항을 만들어줘.

1. 객관식 5문항
2. 서술형 2문항
3. 정답
4. 해설
5. 채점 기준

문항은 단순 암기보다 실제 상황 적용 중심으로 만들어줘.`)}
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>7-3. NotebookLM — 내가 넣은 자료만 근거로 답하는 AI 연구 노트</h3>
        <p style={bodyTextStyle}>
          교육과정 분석, 수행평가 설계, 연수자료 정리, 학생 자료 제작, 질의응답 봇, 오디오 개요, 퀴즈·학습자료 생성에
          활용합니다.
        </p>
        {codeBlock(`이 교육과정 PDF를 바탕으로
중학교 기술 교과에서 AI·정보기술과 연결되는 성취기준을 정리해줘.

표 형식:
성취기준 / 핵심 개념 / 수업 아이디어 / 평가 아이디어`)}
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={h3Style}>7-4. Canvas — Gemini와 함께 문서, 코드, 앱, 인포그래픽을 만드는 작업 공간</h3>
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          수업자료 문서, 인터랙티브 퀴즈, 웹페이지, 인포그래픽, 앱 프로토타입, 코드 설명 등에 활용합니다.
        </p>
      </div>

      <h2 id="순서-8" style={h2Style}>
        8. Claude — Artifacts · Skills · Cowork
      </h2>
      {quote('Claude는 글쓰기 잘하는 AI를 넘어, 문서·코드·업무를 함께 다루는 협업형 AI다.')}

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>8-1. Claude 기본 강점</h3>
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          긴 문서 읽기, 문장 다듬기, 구조화(회의록·평가계획), 학생 산출물 피드백에 강점이 있습니다.
        </p>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>8-2. Claude Artifacts</h3>
        <p style={bodyTextStyle}>대화창 옆에 결과물을 따로 띄워서 보고, 수정하고, 재사용할 수 있는 작업 공간입니다.</p>
        {codeBlock(`중학교 기술 수업용 활동지 웹페이지를 만들어줘.

주제: 지속가능한 도시 설계
대상: 중학교 1학년
구성:
1. 오늘의 학습 목표
2. 모둠별 설계 조건
3. 활동 체크리스트
4. 발표 준비 질문
5. 마무리 성찰 질문

학생들이 보기 쉽게 카드형 디자인으로 만들어줘.`)}
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>8-3. Claude Skills — 반복 업무 처리법을 미리 가르쳐두는 기능</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>구분</th>
              <th style={thStyle}>ChatGPT GPTs</th>
              <th style={thStyle}>Claude Skills</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>개념</td>
              <td style={tdStyle}>맞춤형 챗봇</td>
              <td style={tdStyle}>특정 작업 수행 능력 패키지</td>
            </tr>
            <tr>
              <td style={tdStyle}>초점</td>
              <td style={tdStyle}>대화형 역할 부여</td>
              <td style={tdStyle}>반복 업무 절차화</td>
            </tr>
            <tr>
              <td style={tdStyle}>느낌</td>
              <td style={tdStyle}>&ldquo;전문가 챗봇 만들기&rdquo;</td>
              <td style={tdStyle}>&ldquo;업무 처리법을 Claude에게 장착하기&rdquo;</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>8-4. Claude Cowork — 여러 단계를 대신 수행하는 AI 동료</h3>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>기존 Claude</th>
              <th style={thStyle}>Claude Cowork</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>질문에 답함</td>
              <td style={tdStyle}>업무를 맡아 처리함</td>
            </tr>
            <tr>
              <td style={tdStyle}>한 번에 한 응답</td>
              <td style={tdStyle}>여러 단계를 이어서 수행</td>
            </tr>
            <tr>
              <td style={tdStyle}>문서 요약 중심</td>
              <td style={tdStyle}>자료 수집·정리·문서 생성까지 확장</td>
            </tr>
          </tbody>
        </table>
        {codeBlock(`이 폴더에 있는 연수 이수증 PDF들을 확인해서
다음 항목으로 표를 만들어줘.

- 이름
- 연수명
- 이수 시간
- 이수 날짜
- 발급 기관

확인되지 않는 항목은 '확인 필요'로 표시해줘.`)}
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={h3Style}>8-5. Learning Mode</h3>
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          답을 바로 알려주기보다 학생의 사고를 유도하는 교육형 AI 방식입니다. 장점·개선 질문·다음 확인점을 질문형
          피드백으로 제공합니다.
        </p>
      </div>

      <h2 id="순서-9" style={h2Style}>
        9. Copilot — 이미지 · 영상 · Notebook · Teach
      </h2>
      {quote('Copilot은 Microsoft 생태계 안에서 문서·이미지·영상·수업자료를 만드는 업무형 AI다.')}

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>9-1. Copilot Chat</h3>
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          기본 AI 채팅, 문서 초안, 요약, 평가기준표·가정통신문 초안 작성. 학교 A3 계정으로 접근할 수 있는 경우가
          많지만, PowerPoint 안 자동 PPT 생성(Microsoft 365 Copilot)과는 다릅니다.
        </p>
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>9-2. Copilot 이미지 생성</h3>
        {linkButton(COPILOT_IMAGE_URL, '공식 가이드')}
        {codeBlock(`중학교 공개수업 안내 포스터를 만들어줘.

따뜻한 분위기
학부모 대상
수채화 스타일`)}
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>9-3. Copilot 비디오 생성</h3>
        {linkButton(COPILOT_VIDEO_URL, '공식 가이드')}
        {codeBlock(`중학교 공개수업 홍보 영상을 만들어줘.

30초 분량
학부모 대상
따뜻하고 신뢰감 있는 분위기`)}
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>9-4. Copilot Notebook — Microsoft판 NotebookLM</h3>
        {linkButton(COPILOT_NOTEBOOK_URL, '공식 가이드')}
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          수업 프로젝트 자료 모음, 평가계획·교육과정·활동지 분석, 공개수업 준비 자료 정리에 활용합니다.
        </p>
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={h3Style}>9-5. Copilot Teach — 수업안·퀴즈·평가 자료 생성</h3>
        <p style={bodyTextStyle}>
          Microsoft 365 Copilot 앱의 <strong>교육하기(Teach)</strong>는 강사가 수업을 계획하고, 학습을 평가하고, 수업
          아티팩트를 관리할 수 있도록 AI 기반 도구를 한곳에 모아 둔 기능입니다. 학교 Microsoft 365 교육자 계정에서
          관리자가 Copilot Chat을 켜면 사용할 수 있습니다.
        </p>
        <div style={{ marginBottom: '16px' }}>{linkButton(COPILOT_TEACH_URL, 'Copilot Teach 공식 가이드')}</div>

        <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600, color: 'var(--Color-text-strong)' }}>
          교육하기 도구 개요
        </h4>
        <ul
          style={{
            margin: '0 0 16px 0',
            paddingLeft: '22px',
            lineHeight: 1.8,
            color: 'var(--Color-text-normal)',
            fontSize: '15px',
          }}
        >
          <li>
            <strong>학습 계획 생성기:</strong> 과목, 학년, 학습자 요구에 맞춰 표준에 부합하는 학습 계획을 빠르게
            만듭니다. 교과 자료 첨부와 활동 맞춤 설정을 지원합니다.
          </li>
          <li>
            <strong>퀴즈 작성기:</strong> Microsoft Forms에서 형성평가 또는 총괄평가용 퀴즈를 만듭니다. 질문 유형 선택,
            숙달을 위한 연습 모드, 시간 제한, 표준 부합 등 다양한 옵션을 제공합니다.
          </li>
          <li>
            <strong>루브릭 빌더:</strong> 사전 설정되거나 맞춤형 척도와 기준으로 평가 루브릭을 설계합니다. 표준에
            맞추고 명확성과 차별화를 위해 설명을 편집할 수 있습니다.
          </li>
          <li>
            <strong>플래시카드 생성기:</strong> 용어/정의, 질문/답변, 객관식 형식의 플래시카드 세트를 만듭니다. 심층
            사고를 돕는 힌트와 여러 표시 언어 지원 옵션이 포함되어 있습니다.
          </li>
          <li>
            <strong>AI로 수정하기:</strong> 사용자 자료를 가져와 표준에 맞게 조정하거나 차별화하고, 읽기 수준을
            변경하거나 보조 예시를 추가할 수 있습니다.
          </li>
          <li>
            <strong>기록:</strong> 만든 모든 자료를 저장해 교육자가 이전 초안을 다시 보고 비교할 수 있습니다. 지속적인
            개선 및 공동 작업을 용이하게 합니다.
          </li>
        </ul>
        <p style={{ ...bodyTextStyle, fontSize: '14px', marginBottom: '16px' }}>
          OneDrive, SharePoint 또는 Teams의 핵심 교육 과정 자료를 첨부하면 정확도를 높이고 학교 표준에 맞게 조정할 수
          있습니다.
        </p>

        <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--Color-text-strong)', margin: '0 0 8px 0' }}>
          수업안 프롬프트 예시 (Minecraft Education 지속가능한 도시 설계)
        </p>
        {codeBlock(`중학교 1학년 기술 교과 수업안을 작성해줘.

[수업 주제]
Minecraft Education을 활용한 지속가능한 도시 설계

[대상]
- 중학교 1학년, 4인 1모둠

[수업 시간]
- 45분 1차시

[출력 형식]
표로 작성해줘.
열: 시간 / 교사 활동 / 학생 활동 / Copilot 또는 Minecraft 활용 / 평가 및 피드백`)}
      </div>

      <h2 id="순서-10" style={h2Style}>
        10. Perplexity — 출처를 찾아주는 AI 검색
      </h2>
      {quote('Perplexity는 글을 쓰는 AI라기보다, 출처를 찾아주는 AI 검색 도구에 가깝다.')}
      <table style={{ ...tableStyle, marginBottom: '32px' }}>
        <thead>
          <tr>
            <th style={thStyle}>활용</th>
            <th style={thStyle}>예시</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>최신 동향 조사</td>
            <td style={tdStyle}>AI 교육 트렌드, 에듀테크</td>
          </tr>
          <tr>
            <td style={tdStyle}>정책자료 조사</td>
            <td style={tdStyle}>교육부, 교육청 자료 찾기</td>
          </tr>
          <tr>
            <td style={tdStyle}>논문 탐색</td>
            <td style={tdStyle}>수업 연구, 평가 연구</td>
          </tr>
          <tr>
            <td style={tdStyle}>발표 근거 확보</td>
            <td style={tdStyle}>통계, 기사, 공식 문서</td>
          </tr>
        </tbody>
      </table>

      <h2 id="순서-11" style={h2Style}>
        11. PPT 제작 흐름 — 자료조사 전문가 → PPT 전문가
      </h2>
      <p style={bodyTextStyle}>
        &ldquo;AI 업무활용 연수 PPT 만들어줘&rdquo; 한 번에 요청하면 내용이 얕고, 출처가 약하며, 슬라이드 흐름이
        뻔해집니다.
      </p>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>1단계: 자료조사 전문가</h3>
        <p style={bodyTextStyle}>핵심 정보·통계·사례·최신 트렌드 조사 및 출처 정리</p>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>도구</th>
              <th style={thStyle}>맞춤 기능</th>
              <th style={thStyle}>자료조사 결과</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>ChatGPT</td>
              <td style={tdStyle}>GPTs</td>
              <td style={tdStyle}>Word 파일</td>
            </tr>
            <tr>
              <td style={tdStyle}>Claude</td>
              <td style={tdStyle}>Skill / Artifact</td>
              <td style={tdStyle}>Artifact</td>
            </tr>
            <tr>
              <td style={tdStyle}>Gemini</td>
              <td style={tdStyle}>Gems / Canvas</td>
              <td style={tdStyle}>Canvas</td>
            </tr>
          </tbody>
        </table>
        {codeBlock(`당신은 PPT 제작을 위한 자료조사 전문가입니다. 주제를 입력받으면, PPT 슬라이드에 활용할 수 있는 핵심 정보, 통계, 사례, 최신 트렌드를 조사해 항목별로 정리해주세요. 각 항목마다 출처도 함께 적어주세요.`)}
      </div>

      <div style={infoBoxStyle}>
        <h3 style={h3Style}>2단계: PPT 전문가</h3>
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          자료조사 결과를 바탕으로 슬라이드 구성, 발표 흐름 설계, 슬라이드별 메시지 정리 후 PPT 제작
        </p>
      </div>

      <div style={{ ...infoBoxStyle, marginBottom: '32px' }}>
        <h3 style={h3Style}>3단계: 실제 PPT 제작</h3>
        <p style={{ margin: 0, ...bodyTextStyle, marginBottom: 0 }}>
          PowerPoint, Canva, Gamma, ChatGPT, Copilot 등 제작 도구 활용
        </p>
        <p style={{ margin: '12px 0 0 0', fontSize: '14px', lineHeight: 1.7, color: 'var(--Color-text-normal)' }}>
          먼저 자료조사만 시키고, 그 결과를 PPT 전문가에게 넘기면 내용의 깊이와 발표 흐름이 좋아집니다.
        </p>
      </div>

      <h2 id="순서-12" style={h2Style}>
        12. 바이브코딩과 업무 자동화
      </h2>
      {quote('AI 시대의 핵심은 코딩 실력이 아니라, 문제를 정의하고 원하는 결과를 설명하는 능력이다.')}
      <ul style={{ ...bodyTextStyle, paddingLeft: '22px', marginBottom: '32px' }}>
        <li>이수증 자동 정리</li>
        <li>수업 진도 관리 앱</li>
        <li>물 마시기 앱, 식물 키우기 앱</li>
        <li>MARU 프로젝트</li>
        <li>맞춤형 웹앱 제작</li>
      </ul>

      <h2 id="정리" style={h2Style}>
        최종 메시지
      </h2>
      <div style={{ ...quoteBoxStyle, marginBottom: '16px' }}>
        <p style={{ margin: '0 0 12px 0', fontSize: '15px', lineHeight: 1.8, color: 'var(--Color-text-strong)' }}>
          AI를 잘 쓰는 교사는 프롬프트를 외우는 교사가 아니라,
          <br />
          <strong>업무 문제를 잘 정의하고, 자료와 맥락을 잘 제공하며, AI에게 역할을 나누어 맡기는 교사</strong>다.
        </p>
        <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.8, color: 'var(--Color-text-strong)' }}>
          앞으로의 AI 역량은 &ldquo;말을 잘 거는 능력&rdquo;이 아니라,{' '}
          <strong>AI에게 일을 맡기고, 결과를 검토하고, 안전하게 운영하는 능력</strong>이다.
        </p>
      </div>

      <h2 id="만족도-조사" style={h2Style}>
        연수 만족도 조사
      </h2>
      <div style={{ ...infoBoxStyle, marginBottom: '32px', textAlign: 'center' }}>
        <p style={{ margin: '0 0 16px 0', ...bodyTextStyle }}>
          금일 연수 이수하시느라 고생 많으셨습니다.
          <br />
          마지막으로 연수 만족도 조사에 참여해 주시면 감사하겠습니다.
        </p>
        {linkButton(SATISFACTION_SURVEY_URL, '만족도 조사 (Google Forms)')}
        <p style={{ margin: '16px 0 0 0', fontSize: '13px', color: 'var(--Color-text-normal)', lineHeight: 1.55 }}>
          [서부] 학교로 찾아가는 AI디지털 기반 교육혁신 교원 직무연수 만족도 조사
        </p>
      </div>
    </div>
  );
};

export default JingwanMiddleSchoolAITraining2026;
